export interface ImportLineCommitCoordinates {
  skip?: boolean;
  qty: unknown;
  uom: unknown;
  source_qty: unknown;
  source_uom: unknown;
  source_unit_price: unknown;
  source_line_total: unknown;
}

export type ImportLineCommitIssue =
  | 'stock_qty_invalid'
  | 'stock_uom_invalid'
  | 'source_qty_invalid'
  | 'source_uom_invalid'
  | 'source_unit_price_invalid'
  | 'source_line_total_invalid';

function isFinitePositiveStockQuantity(value: unknown): boolean {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0;
}

function isFinitePositiveNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isNonEmptyUom(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNullableFiniteNonNegative(value: unknown): boolean {
  return value === null
    || (
      typeof value === 'number'
      && Number.isFinite(value)
      && value >= 0
    );
}

/**
 * Contract of the T-481 client payload. Skipped rows are not serialized; every
 * other row must preserve valid document coordinates and valid stock
 * coordinates. In particular, an invalid source value must never be repaired
 * by silently copying the derived stock value.
 */
export function importLineCommitIssue(
  line: ImportLineCommitCoordinates,
): ImportLineCommitIssue | null {
  if (line.skip === true) return null;
  if (!isFinitePositiveStockQuantity(line.qty)) return 'stock_qty_invalid';
  if (!isNonEmptyUom(line.uom)) return 'stock_uom_invalid';
  if (!isFinitePositiveNumber(line.source_qty)) return 'source_qty_invalid';
  if (!isNonEmptyUom(line.source_uom)) return 'source_uom_invalid';
  if (!isNullableFiniteNonNegative(line.source_unit_price)) {
    return 'source_unit_price_invalid';
  }
  if (!isNullableFiniteNonNegative(line.source_line_total)) {
    return 'source_line_total_invalid';
  }
  return null;
}

export function isImportLineCommitReady(
  line: ImportLineCommitCoordinates,
): boolean {
  return importLineCommitIssue(line) == null;
}
